import type { PublicShowCardDetail } from "@/hooks/usePublicShowInventory";
import type { ShowCardVendor, ShowCatalogCard } from "@/lib/shows/showCardFinding";

export const MAX_HUNT_CARDS = 20;

export type ShowHuntStop = {
  vendor: Pick<ShowCardVendor, "participation_id" | "name" | "table_numbers">;
  cards: ShowCatalogCard[];
};

export function parseHuntQueries(value: string, limit = MAX_HUNT_CARDS): string[] {
  const seen = new Set<string>();
  return value.split(/\r?\n/).map((line) => line.trim()).filter((line) => {
    const key = line.toLocaleLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, limit);
}

const tableOrder = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** Greedily covers the most remaining cards, then orders the chosen stops by table. */
export function buildShowHuntStops(details: readonly PublicShowCardDetail[]): ShowHuntStop[] {
  const cards = new Map(details.map((detail) => [detail.card.card_id, detail.card]));
  const remaining = new Set(cards.keys());
  const vendors = new Map<string, { vendor: ShowCardVendor; cardIds: Set<string>; }>();

  for (const detail of details) {
    for (const vendor of detail.vendors) {
      const entry = vendors.get(vendor.participation_id) ?? { vendor, cardIds: new Set<string>() };
      entry.cardIds.add(detail.card.card_id);
      vendors.set(vendor.participation_id, entry);
    }
  }

  const stops: ShowHuntStop[] = [];
  while (remaining.size) {
    const best = [...vendors.values()].sort((a, b) => {
      const coverage = (entry: typeof a) =>
        [...entry.cardIds].filter((cardId) => remaining.has(cardId)).length;
      return coverage(b) - coverage(a)
        || tableOrder.compare(a.vendor.table_numbers[0] ?? "", b.vendor.table_numbers[0] ?? "")
        || a.vendor.name.localeCompare(b.vendor.name);
    })[0];
    if (!best) break;
    const covered = [...best.cardIds].filter((cardId) => remaining.delete(cardId));
    if (!covered.length) break;
    stops.push({
      vendor: {
        participation_id: best.vendor.participation_id,
        name: best.vendor.name,
        table_numbers: best.vendor.table_numbers,
      },
      cards: covered.flatMap((cardId) => {
        const card = cards.get(cardId);
        return card ? [card] : [];
      }),
    });
  }

  return stops.sort((a, b) =>
    tableOrder.compare(a.vendor.table_numbers[0] ?? "", b.vendor.table_numbers[0] ?? "")
    || a.vendor.name.localeCompare(b.vendor.name)
  );
}

/** One stored card's answer from the Show, normalized away from any query-library state. */
export type ShowHuntCardRead = {
  /** The card's listing, when the Show answered with one. */
  detail: PublicShowCardDetail | null;
  /** The Show answered that this card is no longer listed anywhere at it. */
  delisted: boolean;
  /** When that answer landed. 0 when the read has not answered at all. */
  answeredAt: number;
  /** A first read is still in flight. An offline pause has stopped, so it is not in flight. */
  awaiting: boolean;
};

export type ShowHuntRoute = {
  /** Stops built from this session's answers, or null when no live route can be built yet. */
  stops: ShowHuntStop[] | null;
  /** Every stored card answered, so these stops may replace the saved route. */
  complete: boolean;
  /** The newest answer behind `stops`. 0 unless the route is complete. */
  refreshedAt: number;
};

/**
 * Builds the hunt route from each stored card's latest read.
 *
 * A read that never answered, such as a transient failure or an offline pause, affects only its
 * own card, so the stops that did answer stay walkable. A delisted card is dropped even when it
 * still carries the detail from its last successful read, so a card that sold out mid-Show never
 * reaches a table or the saved route. Only a complete read may replace the saved route, because a
 * partial one would drop a card the attendee could still walk to offline.
 */
export function resolveShowHuntRoute(reads: readonly ShowHuntCardRead[]): ShowHuntRoute {
  const answered = reads.filter((read) => read.answeredAt > 0);
  const stops = answered.length > 0 && !reads.some((read) => read.awaiting)
    ? buildShowHuntStops(
      reads.flatMap((read) => read.detail && !read.delisted ? [read.detail] : []),
    )
    : null;
  const complete = reads.length > 0 && answered.length === reads.length;

  return {
    stops,
    complete,
    refreshedAt: complete ? Math.max(...answered.map((read) => read.answeredAt)) : 0,
  };
}
